import Component from '@glimmer/component'
import RouteTemplate from 'ember-route-template'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { fn } from '@ember/helper'
import type RouterService from '@ember/routing/router-service'
import { t } from 'ember-intl'
import { type TeacherHomeRoute } from 'teacher-ui-client/subscription-type/teacher-home/route'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import type DashboardPreferencesService from 'teacher-ui-client/services/dashboard-preferences'
import { on } from '@ember/modifier'
import DragSortList from 'ember-drag-sort/components/drag-sort-list'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { TeacherHomeWidgetId, TeacherHomeWidgetViewModel } from 'teacher-ui-client/teacher-home/widget-registry'
import StarBob from 'teacher-ui-client/components/teacher-home/star-bob.gts'
import { InsightSurveyButton } from 'teacher-ui-client/components/teacher-home/insight-survey-button.gts'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import type { SubscriptionTypeService } from 'teacher-ui-client/services/subscription-type'
import { teacherEventMouseEnter } from 'teacher-ui-client/modifiers/teacher-event'
import { concat } from '@ember/helper'

interface Signature {
  Args: {
    model: ModelFor<TeacherHomeRoute>
  }
}

function reorderIds(widgetIds: TeacherHomeWidgetId[], sourceIndex: number, targetIndex: number): TeacherHomeWidgetId[] {
  const nextWidgetIds = widgetIds.slice()
  const [movedId] = nextWidgetIds.splice(sourceIndex, 1)

  if (!movedId) {
    return widgetIds
  }

  nextWidgetIds.splice(targetIndex, 0, movedId)

  return nextWidgetIds
}

class TeacherHomePage extends Component<Signature> {
  @service('dashboard-preferences') dashboardPreferences!: DashboardPreferencesService
  @service router!: RouterService
  @service subscriptionType!: SubscriptionTypeService
  @service teacherEvents!: TeacherEvents

  @tracked isCustomizing = false
  @tracked hoveredWidgetId?: TeacherHomeWidgetId
  @tracked isDraggingWidget = false
  @tracked isOptingOut = false

  get currentSubscriptionType() {
    return String(this.subscriptionType.current)
  }

  get orderedWidgets() {
    return this.isCustomizing
      ? this.dashboardPreferences.enabledDraftWidgets
      : this.dashboardPreferences.orderedEnabledWidgets
  }

  get availableWidgets() {
    return this.isCustomizing ? this.dashboardPreferences.availableDraftWidgets : []
  }

  get hasWidgets() {
    return this.orderedWidgets.length > 0
  }

  get canOptOut() {
    return this.dashboardPreferences.teacherHomeOptedIn
  }

  openCustomization = () => {
    this.dashboardPreferences.beginEditing()
    this.teacherEvents.sendEvent('TeacherHome:customization_started', this.currentSubscriptionType)
    this.isCustomizing = true
  }

  cancelCustomization = () => {
    this.dashboardPreferences.cancelEditing()
    this.hoveredWidgetId = undefined
    this.isDraggingWidget = false
    this.isCustomizing = false
  }

  saveCustomization = async () => {
    const previouslyEnabled = new Set(this.dashboardPreferences.orderedEnabledWidgets.map(({ id }) => id))
    const draftEnabled = new Set(this.dashboardPreferences.enabledDraftWidgets.map(({ id }) => id))

    await this.dashboardPreferences.save()

    const enabledWidgets = [...draftEnabled].filter((id) => !previouslyEnabled.has(id))
    const disabledWidgets = [...previouslyEnabled].filter((id) => !draftEnabled.has(id))

    this.teacherEvents.sendEvent('TeacherHome:customization_saved', this.currentSubscriptionType, {
      ...this.dashboardPreferences.persistedPreferences,
      enabledWidgets,
      disabledWidgets,
    })
    this.hoveredWidgetId = undefined
    this.isDraggingWidget = false
    this.isCustomizing = false
  }

  optOut = async () => {
    this.isOptingOut = true

    try {
      await this.dashboardPreferences.setTeacherHomeEnabled(false)
      this.teacherEvents.sendEvent('TeacherHome:opted_out', this.currentSubscriptionType)
      void this.router.transitionTo('subscription-type.home', this.args.model.subscriptionType)
    } finally {
      this.isOptingOut = false
    }
  }

  hideWidget = (id: TeacherHomeWidgetId) => {
    this.dashboardPreferences.setDraftWidgetEnabled(id, false)
  }

  addWidget = (id: TeacherHomeWidgetId) => {
    this.dashboardPreferences.setDraftWidgetEnabled(id, true)
  }

  reorderWidgets = ({
    sourceList,
    targetList,
    sourceIndex,
    targetIndex,
  }: {
    sourceList: TeacherHomeWidgetViewModel[]
    targetList: TeacherHomeWidgetViewModel[]
    sourceIndex: number
    targetIndex: number
  }) => {
    this.hoveredWidgetId = undefined
    this.isDraggingWidget = false

    if (sourceList !== targetList || sourceIndex === targetIndex) {
      return
    }

    this.teacherEvents.sendEvent('TeacherHome:widgets_reordered', this.currentSubscriptionType)

    this.dashboardPreferences.reorderDraft(
      reorderIds(
        sourceList.map(({ id }) => id),
        sourceIndex,
        targetIndex,
      ),
    )
  }

  beginWidgetDrag = () => {
    this.hoveredWidgetId = undefined
    this.isDraggingWidget = true
  }

  hoverWidget = (id: TeacherHomeWidgetId) => {
    if (this.isDraggingWidget) {
      return
    }

    this.hoveredWidgetId = id
  }

  unhoverWidget = (id: TeacherHomeWidgetId) => {
    if (this.hoveredWidgetId !== id) {
      return
    }

    this.hoveredWidgetId = undefined
  }

  isWidgetHovered = (id: TeacherHomeWidgetId) => {
    return !this.isDraggingWidget && this.hoveredWidgetId === id
  }

  dragIndicatorClass = (id: TeacherHomeWidgetId) => {
    return [
      'text-neutral-250 inline-grid size-9 place-items-center rounded-full border border-neutral-75 bg-white shadow-sm transition-colors duration-150',
      this.isWidgetHovered(id) ? 'border-neutral-100 bg-neutral-75 text-neutral-300' : '',
    ].join(' ')
  }

  widgetFrameClass = (id: TeacherHomeWidgetId) => {
    return [
      'teacher-home-widget-frame h-full rounded-2xl overflow-visible',
      this.isWidgetHovered(id) ? 'ring-2 ring-oceany-blue-100' : '',
    ].join(' ')
  }

  <template>
    <div class="grow space-y-6" data-test-teacher-home>
      <PageHeader>
        <:title>
          <h1 class="flex"><StarBob height="44" width="44" class="mr-2" /> {{t "teacherHome.page.title"}}</h1>
        </:title>
        <:right>
          {{#unless this.isCustomizing}}
            <div class="flex w-full items-center justify-end gap-4">
              {{#if this.canOptOut}}
                <button
                  type="button"
                  class="cursor-pointer text-sm text-red-300 hover:text-red-600"
                  {{on "click" this.optOut}}
                  disabled={{this.isOptingOut}}
                  data-test-teacher-home-opt-out
                >
                  {{t "teacherHome.page.optOutLabel"}}
                </button>
              {{/if}}

              <InsightSurveyButton />
              <button
                type="button"
                class="border-neutral-75 hover:bg-neutral-75 cursor-pointer rounded-md border bg-white px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:border-neutral-100"
                {{on "click" this.openCustomization}}
                data-test-teacher-home-customise
              >
                {{t "teacherHome.page.customiseLabel"}}
              </button>
            </div>
          {{/unless}}
        </:right>
      </PageHeader>

      <div class="mx-auto max-w-(--breakpoint-xl) space-y-6 px-3">
        {{#if this.isCustomizing}}
          <div
            class="border-oceany-blue-200 bg-oceany-blue-50 relative z-20 rounded-2xl border px-5 py-4"
            data-test-teacher-home-customise-panel
          >
            <div class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p class="text-oceany-blue-350 text-xs font-semibold tracking-wide uppercase">{{t
                    "teacherHome.page.customiseMode.eyebrow"
                  }}</p>
                <h2 class="mt-1 text-lg font-semibold text-neutral-400">{{t
                    "teacherHome.page.customiseMode.title"
                  }}</h2>
                <p class="mt-2 text-sm text-neutral-300">{{t "teacherHome.page.customiseMode.description"}}</p>
              </div>

              <div
                class="relative z-10 flex flex-wrap items-center gap-3 md:justify-end"
                data-test-teacher-home-customise-actions
              >
                <button
                  type="button"
                  class="border-neutral-75 hover:bg-neutral-75 relative z-10 cursor-pointer rounded-md border bg-white px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:border-neutral-100"
                  {{on "click" this.cancelCustomization}}
                  data-test-teacher-home-customise-cancel
                >
                  {{t "teacherHome.page.cancelLabel"}}
                </button>
                <button
                  type="button"
                  class="bg-oceany-blue-300 hover:bg-oceany-blue-350 focus:bg-oceany-blue-350 relative z-10 inline-flex min-w-24 justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm"
                  {{on "click" this.saveCustomization}}
                  data-test-teacher-home-customise-save
                >
                  {{t "teacherHome.page.saveLabel"}}
                </button>
              </div>
            </div>
          </div>
        {{/if}}

        {{#if this.hasWidgets}}
          {{#if this.isCustomizing}}
            <DragSortList
              @items={{this.orderedWidgets}}
              @dragStartAction={{this.beginWidgetDrag}}
              @dragEndAction={{this.reorderWidgets}}
              @isHorizontal={{true}}
              @tagName="div"
              @childTagName="div"
              @childClass="teacher-home-sort-item"
              class="teacher-home-sort-grid"
              data-test-teacher-home-sort-grid
              as |widget|
            >
              <div
                class="relative h-104 overflow-visible"
                {{on "mouseenter" (fn this.hoverWidget widget.id)}}
                {{on "mouseleave" (fn this.unhoverWidget widget.id)}}
                data-test-draft-widget-row={{widget.id}}
                data-test-teacher-home-widget={{widget.id}}
                data-teacher-home-widget-size={{widget.size}}
              >
                <div class="pointer-events-none absolute top-1.5 right-2 z-10 flex items-center gap-2">
                  <div class={{this.dragIndicatorClass widget.id}} aria-hidden="true" data-test-widget-drag-indicator>
                    <PhosphorIcon @icon="phosphor-dots-six-vertical" class="size-4" />
                  </div>

                  <button
                    type="button"
                    class="border-neutral-75 hover:bg-neutral-75 pointer-events-auto cursor-pointer rounded-full border bg-white px-3 py-2 text-sm font-medium text-neutral-300 shadow-sm transition-colors hover:border-neutral-100"
                    {{on "click" (fn this.hideWidget widget.id)}}
                    data-test-hide-widget={{widget.id}}
                  >
                    {{t "teacherHome.page.hideLabel"}}
                  </button>
                </div>

                <div class={{this.widgetFrameClass widget.id}}>
                  {{#let (component widget.component) as |WidgetComponent|}}
                    <WidgetComponent />
                  {{/let}}
                </div>
              </div>
            </DragSortList>
          {{else}}
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {{#each this.orderedWidgets as |widget|}}
                <div
                  class={{widget.gridClass}}
                  {{teacherEventMouseEnter
                    (concat "TeacherHome:widget_mouse_explore:" widget.component)
                    this.currentSubscriptionType
                  }}
                  data-test-teacher-home-widget={{widget.id}}
                >
                  <div class="h-104 overflow-visible">
                    {{#let (component widget.component) as |WidgetComponent|}}
                      <WidgetComponent />
                    {{/let}}
                  </div>
                </div>
              {{/each}}
            </div>
          {{/if}}
        {{else}}
          <div
            class="text-neutral-250 rounded-2xl border border-dashed border-neutral-100 bg-neutral-50 p-8 text-center text-sm"
            data-test-teacher-home-empty-state
          >
            {{t "teacherHome.page.emptyStateDescription"}}
          </div>
        {{/if}}

        {{#if this.isCustomizing}}
          <section class="border-neutral-75 mt-4 space-y-3 rounded-2xl border bg-white p-5">
            <div class="space-y-1">
              <h2 class="text-base font-semibold text-neutral-400">{{t "teacherHome.page.availableWidgets.title"}}</h2>
              <p class="text-neutral-250 text-sm">{{t "teacherHome.page.availableWidgets.description"}}</p>
            </div>

            {{#if this.availableWidgets.length}}
              <div class="space-y-3">
                {{#each this.availableWidgets as |widget|}}
                  <div
                    class="border-neutral-75 flex items-center justify-between gap-3 rounded-xl border bg-white p-4 shadow-sm"
                    data-test-available-widget-row={{widget.id}}
                  >
                    <div>
                      <p class="font-medium text-neutral-400">{{widget.label}}</p>
                      <p class="text-neutral-250 text-sm">{{t "teacherHome.page.availableWidgets.hint"}}</p>
                    </div>

                    <button
                      type="button"
                      class="border-oceany-blue-200 text-oceany-blue-350 hover:border-oceany-blue-300 hover:bg-oceany-blue-50 inline-flex cursor-pointer items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm font-medium transition-colors"
                      {{on "click" (fn this.addWidget widget.id)}}
                      data-test-add-widget={{widget.id}}
                    >
                      <PhosphorIcon @icon="phosphor-plus" />
                      {{t "teacherHome.page.addLabel"}}
                    </button>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div
                class="text-neutral-250 rounded-xl border border-dashed border-neutral-100 bg-neutral-50 p-4 text-sm"
              >
                {{t "teacherHome.page.availableWidgets.emptyState"}}
              </div>
            {{/if}}
          </section>
        {{/if}}
      </div>
    </div>
  </template>
}

export default RouteTemplate(TeacherHomePage)
